import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';

import { useSessionData } from '@confluence/session-data';
import { isUnauthorizedError } from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';

const CONFLUENCE_FIRST_ACTIVE_TRAIT = 'confluence_first_active';

type UseFirstActiveDateArgs = { skip?: boolean };

export const useFirstActiveDate = (args: UseFirstActiveDateArgs = {}) => {
	const { skip = false } = args;
	const { cloudId, userId } = useSessionData();
	const { data, loading, error } = useQuery(
		gql`
			query useFirstActiveDateQuery($cloudId: String!, $userId: String!) @experimental {
				experimentalSiteUserPersonalization(cloudId: $cloudId, userId: $userId) {
					attributes {
						name
						value
					}
				}
			}
		`,
		{
			variables: { cloudId, userId },
			skip: !userId || skip,
			onError: (e) => {
				if (!isUnauthorizedError(e)) {
					markErrorAsHandled(e);
				}
			},
		},
	);

	const attributes = data?.experimentalSiteUserPersonalization?.attributes;
	const firstActiveValue = attributes?.find(
		({ name }: { name: string }) => name === CONFLUENCE_FIRST_ACTIVE_TRAIT,
	)?.value;
	// Assuming firstActiveValue is in a compatible date time string format
	const firstActiveDate = new Date(firstActiveValue);
	const isValidDate = !isNaN(firstActiveDate.getTime());
	const isSafeToReturn = isValidDate && !loading && !Boolean(error);

	return {
		loading,
		error,
		data: isSafeToReturn ? firstActiveDate : null,
	};
};
